import {
  ArrowDownUpIcon,
  ArrowLeftRightIcon,
  BanknoteIcon,
  Building2Icon,
  ClipboardListIcon,
  GaugeIcon,
  NetworkIcon,
  TargetIcon,
  WalletIcon,
} from "lucide-react";

import { Values } from "@/constants/values";
import type { ReportSourceObjectType } from "@/objects/@types";

/**
 * The sources a report can be made of that are not record types, by name.
 *
 * A cash flow is not a thing the ledger holds - the server works one out - so
 * these are not nodes and cannot be opened, filtered or referenced. They are
 * declared beside the records because the catalogue a widget is dragged out
 * of asks the same two things of every source, and keeping them out of
 * ``Objects`` keeps that registry what it is: the types a node can be.
 */
export const ReportObjects: Record<string, ReportSourceObjectType> = {
  balance_sheet: {
    ReactIcon: ClipboardListIcon,
    report: {
      source: "balance_sheet",
      description: "What is owned, what is owed, and what is left",
      asks: "What is the business worth as it stands?",
    },
  },
  bank_accounts: {
    ReactIcon: WalletIcon,
    report: {
      source: "bank_accounts",
      description: "Every account and what is in it right now",
      asks: "How much cash do we have, and where is it?",
    },
  },
  budgets: {
    ReactIcon: TargetIcon,
    report: {
      source: "budgets",
      description: "What was planned to be spent, against what was",
      asks: "Which budgets are we running over?",
    },
  },
  ...(Values.IS_BUDGET_SHOWN && {
    budget: {
      ReactIcon: TargetIcon,
      report: {
        source: "budget",
        description:
          "What each line of the budget planned and spent, month by month",
        asks: "Where did we spend more than we planned this month?",
      },
    },
  }),
  cash_flow: {
    ReactIcon: ArrowDownUpIcon,
    report: {
      source: "cash_flow",
      description: "What came in and what went out, period by period",
      asks: "Did we end the quarter with more cash than we started with?",
    },
  },
  chart_of_accounts: {
    ReactIcon: NetworkIcon,
    report: {
      source: "chart_of_accounts",
      description: "The whole ledger as a tree, with what each account holds",
      asks: "What sits under each part of the books?",
    },
  },
  inter_entity_balances: {
    ReactIcon: Building2Icon,
    report: {
      source: "inter_entity_balances",
      description: "What each of your businesses owes the others",
      asks: "Which entity is carrying the others?",
    },
  },
  money_in_out: {
    ReactIcon: ArrowLeftRightIcon,
    report: {
      source: "money_in_out",
      description: "What came in against what went out, month by month",
      asks: "Did we take in more than we spent last month?",
    },
  },
  overview_kpis: {
    ReactIcon: GaugeIcon,
    report: {
      source: "overview_kpis",
      description: "The few figures worth seeing first",
      asks: "How are we doing, at a glance?",
    },
  },
  shareholder_loans: {
    ReactIcon: BanknoteIcon,
    report: {
      source: "shareholder_loans",
      description: "What the owners have put in and taken out",
      asks: "Does the company owe me money, or do I owe it?",
    },
  },
};
